import React, { useMemo, useState } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ShareIcon from "@mui/icons-material/Share";

import { postUsuario } from "../../../services/apis";
import { AlertError } from "../../../utils/componentes-utiles/AlertError";
import { ContasenaOculta } from "../../../utils/componentes-utiles/ContasenaOculta";
import { generarColorAleatorio } from "../../../utils/utils";
import { ClienteMultiSelect } from "./ClienteMultiSelect";
import { DelegacionMultiSelect } from "./DelegacionMultiSelect";

const ROL_CLIENTE = 990;
const ROL_CONTROLADOR = 980;

const normalizeRoleName = (value = "") =>
	String(value ?? "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();

const generateRandomPassword = (length = 12) => {
	const characters = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%";
	let result = "";

	for (let index = 0; index < length; index += 1) {
		result += characters.charAt(Math.floor(Math.random() * characters.length));
	}

	return result;
};

export const FormCrearUsuario = ({
	open,
	setOpen,
	cargarUsuarios,
	roles = [],
	departamentos = [],
	clientes = [],
	delegaciones = [],
}) => {
	const createInitialPassword = () => generateRandomPassword();

	const [usuario, setUsuario] = useState({
		nombre: "",
		apellidos: "",
		mail: "",
		telefono: "",
		nif: "",
		id_rol: "",
		id_departamento: "",
		color: generarColorAleatorio(),
		id_clientes: [],
		id_delegaciones: [],
	});
	const [contrasena, setContrasena] = useState(createInitialPassword);
	const [contrasenaCon, setContrasenaCon] = useState(contrasena);
	const [error, setError] = useState("");
	const [shareFeedback, setShareFeedback] = useState("");
	const [loading, setLoading] = useState(false);

	const esCliente = Number(usuario.id_rol || 0) === ROL_CLIENTE;
	const esControlador = Number(usuario.id_rol || 0) === ROL_CONTROLADOR;
	const rolSeleccionado = useMemo(
		() => roles.find((rol) => Number(rol?.id) === Number(usuario.id_rol || 0)) || null,
		[roles, usuario.id_rol]
	);
	const esCaboCliente = normalizeRoleName(rolSeleccionado?.nombre) === "cabo cliente";
	const color = usuario.color || generarColorAleatorio();

	const handleClose = (force = false) => {
		if (loading && !force) {
			return;
		}

		setOpen(false);
		const nextPassword = createInitialPassword();
		setUsuario({
			nombre: "",
			apellidos: "",
			mail: "",
			telefono: "",
			nif: "",
			id_rol: "",
			id_departamento: "",
			color: generarColorAleatorio(),
			id_clientes: [],
			id_delegaciones: [],
		});
		setContrasena(nextPassword);
		setContrasenaCon(nextPassword);
		setError("");
		setShareFeedback("");
	};

	const handleChange = (event) => {
		const { name, value } = event.target;
		setUsuario((current) => {
			const nextUsuario = {
				...current,
				[name]: value,
			};

			const selectedRole = roles.find((rol) => Number(rol?.id) === Number(value)) || null;
			const nextRoleName = normalizeRoleName(selectedRole?.nombre);

			if (
				name === "id_rol" &&
				Number(value) !== ROL_CLIENTE &&
				Number(value) !== ROL_CONTROLADOR &&
				nextRoleName !== "cabo cliente"
			) {
				nextUsuario.id_clientes = [];
			}

			if (
				name === "id_rol" &&
				Number(value) !== ROL_CONTROLADOR &&
				nextRoleName !== "cabo cliente"
			) {
				nextUsuario.id_delegaciones = [];
			}

			return nextUsuario;
		});
		setError("");
	};

	const clientesAsignadosTexto = useMemo(() => {
		if ((!esCliente && !esCaboCliente && !esControlador) || usuario.id_clientes.length === 0) {
			return "";
		}

		const nombres = clientes
			.filter((cliente) => usuario.id_clientes.includes(Number(cliente.id)))
			.map((cliente) => cliente.nombre)
			.filter(Boolean);

		return nombres.join(", ");
	}, [clientes, esCaboCliente, esCliente, esControlador, usuario.id_clientes]);

	const delegacionesAsignadasTexto = useMemo(() => {
		if ((!esCaboCliente && !esControlador) || usuario.id_delegaciones.length === 0) {
			return "";
		}

		const nombres = delegaciones
			.filter((delegacion) => usuario.id_delegaciones.includes(Number(delegacion.id)))
			.map((delegacion) => delegacion.nombre)
			.filter(Boolean);

		return nombres.join(", ");
	}, [delegaciones, esCaboCliente, esControlador, usuario.id_delegaciones]);

	const regeneratePassword = () => {
		const nextPassword = createInitialPassword();
		setContrasena(nextPassword);
		setContrasenaCon(nextPassword);
		setShareFeedback("");
	};

	const buildShareText = () => {
		const fullName = [usuario.nombre, usuario.apellidos].filter(Boolean).join(" ").trim();
		return [
			fullName ? `Usuario: ${fullName}` : null,
			usuario.mail ? `Email: ${usuario.mail}` : null,
			`Contraseña: ${contrasena}`,
		]
			.filter(Boolean)
			.join("\n");
	};

	const handleShareCredentials = async () => {
		if (!contrasena.trim()) {
			setError("No hay contraseña disponible para compartir.");
			return;
		}

		const shareText = buildShareText();

		try {
			if (navigator.share) {
				await navigator.share({
					title: "Credenciales de acceso",
					text: shareText,
				});
				return;
			}

			if (navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(shareText);
				setShareFeedback("Credenciales copiadas al portapapeles.");
				return;
			}

			window.prompt("Copia estas credenciales:", shareText);
		} catch (shareError) {
			if (shareError?.name === "AbortError") {
				return;
			}

			console.error("No se pudieron compartir las credenciales:", shareError);
			setError("No se pudieron compartir las credenciales.");
		}
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		if (!usuario.nombre || !usuario.mail || !usuario.id_rol) {
			setError("Falta rellenar algun campo obligatorio.");
			return;
		}

		if (contrasena.trim() === "" || contrasenaCon.trim() === "") {
			setError("Debes indicar la contraseña y su confirmacion.");
			return;
		}

		if (contrasena !== contrasenaCon) {
			setError("Las contraseñas no coinciden.");
			return;
		}

		try {
			setLoading(true);

			const payload = {
				...usuario,
				color,
				contrasena,
				id_clientes: esCliente || esCaboCliente || esControlador ? usuario.id_clientes : [],
				id_delegaciones: esCaboCliente || esControlador ? usuario.id_delegaciones : [],
			};

			const response = await postUsuario(payload);
			if (response?.success === true) {
				await cargarUsuarios();
				handleClose(true);
				return;
			}

			setError(response?.error || "No se pudo crear el usuario.");
		} catch (submitError) {
			console.error("Error al crear usuario:", submitError);
			setError("Error de conexion con el servidor.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullWidth
			maxWidth="md"
			aria-labelledby="form-dialog-title"
		>
			<DialogTitle id="form-dialog-title">
				<Grid
					container
					size={{ xs: 12 }}
					spacing={2}
					direction="row"
					alignItems="center"
					justifyContent="space-between"
				>
					Nuevo/a Usuario/a
					<CancelIcon onClick={handleClose} style={{ cursor: "pointer" }} />
				</Grid>
			</DialogTitle>

			<DialogContent>
				<form onSubmit={handleSubmit}>
					<Grid container spacing={2} sx={{ pt: 0.5 }}>
						<Grid item size={{ xs: 12, md: 4 }}>
							<Grid container direction="column" alignItems="center" gap={1}>
								<Typography variant="body2">Color del usuario</Typography>
								<AccountCircleIcon sx={{ fontSize: 90, color }} />
								<input
									type="color"
									value={color}
									onChange={(event) =>
										setUsuario((current) => ({ ...current, color: event.target.value }))
									}
								/>
							</Grid>
						</Grid>

						<Grid item size={{ xs: 12, md: 8 }}>
							<Grid container spacing={2}>
								<Grid item size={{ xs: 12, md: 6 }}>
									<TextField
										label="Nombre"
										fullWidth
										name="nombre"
										value={usuario.nombre}
										onChange={handleChange}
										required
									/>
								</Grid>
								<Grid item size={{ xs: 12, md: 6 }}>
									<TextField
										label="Apellidos"
										fullWidth
										name="apellidos"
										value={usuario.apellidos}
										onChange={handleChange}
									/>
								</Grid>
								<Grid item size={{ xs: 12, md: 6 }}>
									<TextField
										label="Mail"
										fullWidth
										type="email"
										name="mail"
										value={usuario.mail}
										onChange={handleChange}
										required
									/>
								</Grid>
								<Grid item size={{ xs: 12, md: 6 }}>
									<TextField
										label="Telefono"
										fullWidth
										name="telefono"
										value={usuario.telefono}
										onChange={handleChange}
									/>
								</Grid>
								<Grid item size={{ xs: 12, md: 6 }}>
									<TextField
										label="NIF"
										fullWidth
										name="nif"
										value={usuario.nif}
										onChange={handleChange}
									/>
								</Grid>
								<Grid item size={{ xs: 12, md: 6 }}>
									<FormControl fullWidth>
										<InputLabel>Rol</InputLabel>
										<Select
											value={usuario.id_rol}
											name="id_rol"
											onChange={handleChange}
											label="Rol"
										>
											{roles.map((rol) => (
												<MenuItem key={rol.id} value={rol.id}>
													{rol.nombre}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>
							</Grid>
						</Grid>

						<Grid item size={{ xs: 12, md: 6 }}>
							<ContasenaOculta
								contrasena={contrasena}
								setContrasena={setContrasena}
								label="Contraseña"
								inputId="crear-usuario-password"
							/>
						</Grid>
						<Grid item size={{ xs: 12, md: 6 }}>
							<ContasenaOculta
								contrasena={contrasenaCon}
								setContrasena={setContrasenaCon}
								label="Confirmar contraseña"
								inputId="crear-usuario-password-confirm"
							/>
						</Grid>
						<Grid
							item
							size={{ xs: 12 }}
							sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
						>
							<Button
								variant="outlined"
								startIcon={<AutorenewIcon />}
								onClick={regeneratePassword}
								disabled={loading}
							>
								Regenerar contraseña
							</Button>
							<Button
								variant="outlined"
								startIcon={<ShareIcon />}
								onClick={handleShareCredentials}
								disabled={loading}
							>
								Compartir
							</Button>
						</Grid>
						{shareFeedback ? (
							<Grid item size={{ xs: 12 }}>
								<Typography variant="body2" color="success.main">
									{shareFeedback}
								</Typography>
							</Grid>
						) : null}

						{(esCliente || esCaboCliente || esControlador) && (
							<Grid item size={{ xs: 12 }}>
								<ClienteMultiSelect
									clientes={clientes}
									value={usuario.id_clientes}
									onChange={(id_clientes) =>
										setUsuario((current) => ({ ...current, id_clientes }))
									}
									helperText={
										esControlador
											? "El controlador podra ver los partes de los clientes asignados."
											: esCaboCliente
											? "Este perfil podra ver los partes de los clientes asignados."
											: "El usuario con rol cliente solo podra ver los partes de estos clientes."
									}
								/>
							</Grid>
						)}

						{(esCaboCliente || esControlador) && (
							<Grid item size={{ xs: 12 }}>
								<DelegacionMultiSelect
									delegaciones={delegaciones}
									value={usuario.id_delegaciones}
									onChange={(id_delegaciones) =>
										setUsuario((current) => ({ ...current, id_delegaciones }))
									}
									helperText={
										esControlador
											? "El controlador tambien podra ver los partes de las delegaciones asignadas."
											: "Tambien podra ver los partes de las delegaciones asignadas."
									}
								/>
							</Grid>
						)}

						{(esCliente || esCaboCliente || esControlador) && clientesAsignadosTexto && (
							<Grid item size={{ xs: 12 }}>
								<Typography variant="body2" color="text.secondary">
									Clientes seleccionados: {clientesAsignadosTexto}
								</Typography>
							</Grid>
						)}

						{(esCaboCliente || esControlador) && delegacionesAsignadasTexto && (
							<Grid item size={{ xs: 12 }}>
								<Typography variant="body2" color="text.secondary">
									Delegaciones seleccionadas: {delegacionesAsignadasTexto}
								</Typography>
							</Grid>
						)}
					</Grid>
				</form>
			</DialogContent>

			<DialogActions>
				<Button onClick={handleClose} color="primary" disabled={loading}>
					Cancelar
				</Button>
				<Button
					onClick={handleSubmit}
					color="primary"
					variant="contained"
					disabled={loading}
				>
					{loading ? "Creando..." : "Crear Usuario"}
				</Button>
			</DialogActions>

			{error ? (
				<AlertError open={true} setOpen={() => setError("")} titulo="Error" mensaje={error} />
			) : null}
		</Dialog>
	);
};
